import {
	Button,
	MenuItemCheckbox,
	mergeClasses,
	MenuGroupHeader,
	MenuDivider,
} from "@fluentui/react-components";
import { InteractionType, TableType } from "@/common/types";
import { useCallback, useMemo, useState, useEffect } from "react";
import { useIntl } from "react-intl";
import { useInteractionMenuStyle } from "@/components/molecules/interactionMenu/style";
import { useInteractionStyle } from "@/components/organisms/interaction/style";
import { Flex } from "@/components/atoms/flex";
import { MenuListPopover } from "@/components/atoms/menuList";
import { allInteractions } from "@/components/molecules/interactionMenu";
import { InteractionsIconMapMedium } from "@/common/helpers";
import { Filter20Regular } from "@fluentui/react-icons";
import { SupervisorTypeFilterProps } from "./types";
import {
	InteractionFilterActions,
	useInteractionFilterStore,
} from "@/common/stores/nonpersistingStores/interactionfilter";
import { InteractionFilterData } from "@/repository/models";
import {
	AppCacheActions,
	AppCacheState,
	CacheKey,
	useAppCacheStore,
} from "@/common/stores/persistingStores/app";

export const SupervisorTypeFilter = (props: SupervisorTypeFilterProps) => {
	const { cacheKey, type, filterData } = props;
	const intl = useIntl();
	const menuClasses = useInteractionMenuStyle();
	const classes = useInteractionStyle();
	const typeFilterKey = "typeFilterKey";
	const appStoreCache = useAppCacheStore((state: AppCacheState) => state.cache);
	const appStoreActions = useAppCacheStore((state: AppCacheActions) => state);
	const filterDataActions = useInteractionFilterStore(
		(actions: InteractionFilterActions) => actions
	);

	const interactionName = useCallback(
		(type: InteractionType) =>
			intl.formatMessage({ id: `Interaction.${InteractionType[type]}` }),
		[intl]
	);

	const getInitialSelectedTypesByCacheKey = useCallback(() => {
		if (cacheKey && appStoreCache[cacheKey]) {
			return appStoreCache[cacheKey].types;
		} else {
			return [];
		}
	}, []);

	const [selectedTypes, setSelectedTypes] = useState<string[]>(
		getInitialSelectedTypesByCacheKey()
	);

	useEffect(() => {
		if (
			cacheKey &&
			((appStoreCache[cacheKey] &&
				selectedTypes !== appStoreCache[cacheKey].queueIds) ||
				!appStoreCache[cacheKey])
		) {
			const filterDataModel = {
				queueIds: filterData ? filterData.queueIds : [],
				types: selectedTypes,
			} as InteractionFilterData;

			appStoreActions.setCache(cacheKey as CacheKey, filterDataModel);

			setFilterData(filterDataModel);
		}
	}, [selectedTypes]);

	const manageSelectedTypes = useCallback(
		(type: InteractionType) => {
			if (selectedTypes.includes(type.toString())) {
				setSelectedTypes(selectedTypes.filter((x) => x !== type.toString()));
			} else {
				setSelectedTypes((prevState) => [...prevState, ...[type.toString()]]);
			}
		},
		[selectedTypes]
	);

	const setFilterData = useCallback((filterData: InteractionFilterData) => {
		switch (type) {
			case TableType.Active:
				filterDataActions.setActive(filterData);
				break;
			case TableType.None:
				filterDataActions.setWaiting(filterData);
				break;
			case TableType.Waiting:
			default:
				break;
		}
	}, []);

	const resetTypes = useCallback(() => setSelectedTypes([]), []);

	const menuGroupHeaderItem = useCallback(
		(filterMessage: string) => [
			<MenuGroupHeader
				key="interactionHeader"
				className={menuClasses.filterGroupHeader}
			>
				{filterMessage}
				<Button
					appearance="transparent"
					className={menuClasses.cleanButtonSize}
					onClick={() => {
						resetTypes();
					}}
				>
					{intl.formatMessage({ id: "InteractionMenu.Reset" })}
				</Button>
			</MenuGroupHeader>,
			<MenuDivider key="interactionDivider" />,
		],
		[allInteractions]
	);

	const filterInteractionItems = useMemo<JSX.Element[]>(
		() => [
			...menuGroupHeaderItem(
				intl.formatMessage({ id: "InteractionMenu.ShowOnly" })
			),
			...allInteractions.map((type: InteractionType) => (
				<MenuItemCheckbox
					key={type}
					name={typeFilterKey}
					icon={InteractionsIconMapMedium[type]}
					value={type.toString()}
					className={mergeClasses(
						menuClasses.filterCheckboxItem,
						classes.leftAlign
					)}
					onClick={() => {
						manageSelectedTypes(type);
					}}
				>
					{interactionName(type)}
				</MenuItemCheckbox>
			)),
		],
		[allInteractions, selectedTypes]
	);

	return (
		(type === TableType.Active || type === TableType.None) && (
			<Flex>
				<MenuListPopover
					classesName={{
						menuList: menuClasses.popoverContent,
						menuPopover: menuClasses.menuPopover,
					}}
					checkedValues={{
						[typeFilterKey]: selectedTypes,
					}}
					onCheckedValueChange={(e) => {
						e.preventDefault();
					}}
					positioning="above-end"
					trigger={
						selectedTypes.length ? (
							<Button
								shape="circular"
								onClick={(e) => {
									e.preventDefault();
								}}
								className={classes.filterButton}
							>
								<div className={classes.filterFontWeight}>
									{selectedTypes.length}
								</div>
								<Filter20Regular className={classes.filterButtonIcon} />
							</Button>
						) : (
							<Button
								appearance="transparent"
								icon={<Filter20Regular />}
								onClick={(e) => {
									e.preventDefault();
								}}
							/>
						)
					}
					items={filterInteractionItems}
				/>
			</Flex>
		)
	);
};
